import { gql } from '@/__generated__/gql';
import {
  GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery,
  Category as gqlCategory,
  Eligibility as gqlEligibility,
  EntityType as gqlEntityType,
  TaxStatus
} from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { InvestmentVehicleDto } from '@/components/dealPipeline-page/modify/types';
import { INVESTMENT_VEHICLES_QUERY } from '@/components/dealPipeline-page/queries';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { UPDATE_LEGAL_ENTITY_MUTATION } from '@/components/investors-page/editInvestorPage/queries';
import { EditInvestorEntityDetailsTaxStatusOptionEnum } from '@/components/investors-page/editInvestorPage/types';
import {
  GET_LEGAL_ENTITY_DOCUMENTATION_QUERY,
  UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION,
  UPSERT_CONTACT_LEGAL_ENTITY_MUTATION
} from '@/components/investors-page/investorDetailsPage/tabs/documentationTab/queries';
import { ITenant } from '@/components/investors-page/types';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import FooterModal from '@/components/settings-page/importData/footer';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { PROPOSAL_TYPE } from '@/types/investments';
import { ApolloError, NetworkStatus, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  EntityType,
  evaluateDependsOn,
  FormTemplate,
  generateSchemaFromTemplate,
  processExpectedAttachments,
  SubscriptionDocumentSignatureType,
  TenantType
} from '@trace-8/trace8-schema';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { updateWizardSteps, WizardProgressBar } from '../../basicComponents/wizard/progressBar';
import { EXECUTE_ACTION_MUTATION, UPDATE_ALLOCATION_AMOUNT_MUTATION, UPDATE_ALLOCATION_INVESTMENT_VEHICLE_MUTATION } from '../queries';
import { AddAttachmentFileModalPage } from './addAttachmentFileModalPage';
import MissingAttachmentsWarning from './addAttachmentFileModalPage/modals/missingAttachmentsWarning';
import { Details } from './details';
import { EnterBankAccountInfoModalPage } from './enterBankAccountInfoModalPage';
import { EnterContactInfoModalPage } from './enterContactInfoModalPage';
import { GET_BANK_ACCOUNTS_AND_CONTACTS_QUERY, RENDERING_TEMPLATES_QUERY } from './queries';
import { IInvestorDetailsStepForm, InvestorDetailsStep, investorDetailsStepDefaultValues } from './steps/investorDetails';
import { SignatureMethodStep } from './steps/signatureMethod';
import { ITransactionDetailsStepForm, TransactionDetailsStep } from './steps/transactionDetails';
import { RowComponent } from './templateComponents';
import { isBankAccountDataComplete } from './templateComponents/rowComponents/bankAccountsComponent';
import { isContactDataComplete } from './templateComponents/rowComponents/contactsComponent';
import {
  IDocumentationAttachment,
  IRenderingTemplate,
  IRenderTemplate,
  IRenderTemplatePage,
  IRenderTemplateRow,
  OpenWizardModalPageArgsType
} from './templateComponents/types';

const getInitialWizardSteps = (templateData: IRenderTemplate | null): { name: string; status: string }[] => {
  if (!templateData) return [];
  const inititalSteps = templateData.pages.map((page) => {
    return { name: page.title, status: 'NOT_STARTED' };
  });
  return [
    { name: 'Investor Details', status: 'IN_PROGRESS' },
    { name: 'Transaction Details', status: 'NOT_STARTED' },
    ...inititalSteps,
    { name: 'Signature Method', status: 'NOT_STARTED' }
  ];
};

const getWizardSteps = (templateData: IRenderTemplate | null): { name: string; status: string }[] => {
  if (!templateData) return [];
  const inititalSteps = templateData.pages.map((page, index) => {
    if (index === 0) {
      return { name: page.title, status: 'IN_PROGRESS' };
    }
    return { name: page.title, status: 'NOT_STARTED' };
  });
  return [
    { name: 'Investor Details', status: 'COMPLETED' },
    { name: 'Transaction Details', status: 'COMPLETED' },
    ...inititalSteps,
    { name: 'Signature Method', status: 'NOT_STARTED' }
  ];
};

export const cleanFormDataByConfig = (pages: IRenderTemplatePage[], formData: any, withExpectedData: boolean = true): any => {
  const updatedFormData: any = {};

  pages.forEach((page) => {
    updatedFormData[page.name] = {};

    if ('expectedData' in page && withExpectedData) {
      page.expectedData?.forEach((expectedDataItem: any) => {
        updatedFormData[page.name][expectedDataItem.name] = formData?.[page.name]?.[expectedDataItem.name];
      });
    }
    page.rows.forEach((row) => {
      const shouldRender = row.dependsOn && formData?.[page.name] ? evaluateDependsOn(row.dependsOn, formData?.[page.name]) : true;
      if (!shouldRender) return;

      const rowKeys = Object.keys(formData?.[page.name] ?? {});

      switch (row.component) {
        case 'custom':
          if ('fields' in row) {
            row.fields.forEach((field) => {
              if (rowKeys.includes(field.name) && formData?.[page.name] && formData?.[page.name]?.[field.name] !== undefined) {
                updatedFormData[page.name][field.name] = formData?.[page.name]?.[field.name];
              }
            });
          }
          break;
        case 'checkboxGroup':
          if ('items' in row.config) {
            row.config.items.forEach((item: any) => {
              if (rowKeys.includes(item.name) && formData?.[page.name] && formData?.[page.name]?.[item.name] !== undefined) {
                updatedFormData[page.name][item.name] = formData?.[page.name]?.[item.name];
              }
            });
          }
          if ('sections' in row.config) {
            row.config.sections.forEach((section: any) => {
              section.items.forEach((item: any) => {
                if (rowKeys.includes(item.name) && formData?.[page.name] && formData?.[page.name]?.[item.name] !== undefined) {
                  updatedFormData[page.name][item.name] = formData?.[page.name]?.[item.name];
                }
              });
            });
          }
          break;
        case 'attachments':
          if (rowKeys.includes(row.name) && formData?.[page.name] && formData?.[page.name]?.[row.name] !== undefined) {
            if (!_.isArray(formData?.[page.name]?.[row.name])) {
              updatedFormData[page.name][row.name] = formData?.[page.name]?.[row.name];
            }
          }
          break;
        default:
          if (rowKeys.includes(row.name) && formData?.[page.name] && formData?.[page.name]?.[row.name] !== undefined) {
            updatedFormData[page.name][row.name] = formData?.[page.name]?.[row.name];
          }
          break;
      }
    });
  });

  return updatedFormData;
};

const GET_ALLOCATION_QUERY = gql(`
  query GenerateSignaturePacketPageAllocation($id: String!) {
    Allocation(id: $id) {
      id
      committedCapital
      proposalType
      investment {
        id
        name
        investorDocsDue
        minimum
      }
      legalEntity {
        id
        entityName
        taxExempt
        entityType
        otherType
        family {
          id
        }
      }
      investmentVehicle {
        id
        name
        taxExempt
      }
      operationsWorkflowTransitionTask {
        id
        availableActions
      }
    }
  }
`);

const GET_TENANT_QUERY = gql(`
  query GenerateSignaturePacketPageGetInvestor($data: getInvestorDto!) {
    getInvestor(data: $data) {
      tenant {
        id
        name
      }
    }
  }
`);

const GET_INVESTOR_DETAILS_DATA = gql(`
  query GenerateSignaturePacketPageLegalEntity($id: String!) {
    legalEntity(id: $id) {
      id
      category
      entityType
      eligibility
      otherType
      taxExempt
      taxStatus
    }
  }
`);

const FILE_TOO_LARGE_ERROR_MESSAGE =
  'The total size of all attachments exceeds the maximum allowed by Docusign (25MB). Please remove some of the attachments to continue.';

export const GenerateSignaturePacket = () => {
  const { user } = useAuth();
  const params = useParams();
  const goBack = useGoBack();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { t } = useTranslation(['common', 'generateSignaturePacketPage']);

  const isPlatformManagerUser = user && user.tenant?.type === TenantType.PLATFORM_MANAGER;

  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [renderingTemplates, setRenderingTemplates] = useState<IRenderingTemplate[]>([]);
  const [currentStepPage, setCurrentStepPage] = useState<IRenderTemplatePage | null>(null);
  const schema = template ? generateSchemaFromTemplate({ ...template, pages: [currentStepPage] } as FormTemplate) : null;

  const methods = useForm<Record<string, any>>({
    mode: 'onChange',
    resolver: schema ? zodResolver(schema) : null
  });

  const [signaturePacketWizardSteps, setSignaturePacketWizardSteps] = useState([]);
  const [modalPage, setModalPage] = useState({ isOpen: false, type: '' });
  const [currentRow, setCurrentRow] = useState<IRenderTemplateRow | null>(null);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [ids, setIds] = useState<{ investmentId: string; familyId: string; entityId: string; tenantId: string; templateId: string }>({
    investmentId: '',
    familyId: '',
    entityId: '',
    tenantId: '',
    templateId: ''
  });
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [bankAccounts, setBankAccounts] = useState<GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts']>(
    []
  );
  const [contacts, setContacts] = useState<GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts']>([]);
  const [currentAttachment, setCurrentAttachment] = useState<IDocumentationAttachment | null>(null);
  const [currentAttachmentSectionInfo, setCurrentAttachmentSectionInfo] = useState({ sectionName: '', expectedAttachmentName: '', contactId: '' });
  const [investmentVehicles, setInvestmentVehicles] = useState<InvestmentVehicleDto[]>([]);
  const [investorDetailsStepData, setInvestorDetailsStepData] = useState<IInvestorDetailsStepForm>(investorDetailsStepDefaultValues);
  const [kyc, setKyc] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoadingDocumentation, setIsLoadingDocumentation] = useState(false);
  const [isContactAndBankAccountComplete, setIsContactAndBankAccountComplete] = useState(true);
  const [scrollTo, setScrollTo] = useState(0);

  const [executeAction] = useMutation(EXECUTE_ACTION_MUTATION);
  const [updateAllocationAmount] = useMutation(UPDATE_ALLOCATION_AMOUNT_MUTATION);
  const [updateAllocationInvestmentVehicle] = useMutation(UPDATE_ALLOCATION_INVESTMENT_VEHICLE_MUTATION);
  const [updateLegalEntityDocumentation] = useMutation(UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION);
  const [updateEntity] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION);
  const [getLegalEntityDocumentation] = useLazyQuery(GET_LEGAL_ENTITY_DOCUMENTATION_QUERY);
  const [getRenderingTemplates] = useLazyQuery(RENDERING_TEMPLATES_QUERY);
  const [upsertContactLegalEntity] = useMutation(UPSERT_CONTACT_LEGAL_ENTITY_MUTATION);

  const {
    data: allocationData,
    loading: allocationLoading,
    refetch: allocationRefetch,
    networkStatus: allocationNetworkStatus
  } = useQuery(GET_ALLOCATION_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: params?.allocationId
    },
    onCompleted: (data) => {
      setIds({
        ...ids,
        investmentId: data?.Allocation?.investment?.id ?? '',
        familyId: data?.Allocation?.legalEntity?.family?.id ?? '',
        entityId: data?.Allocation?.legalEntity?.id ?? ''
      });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { networkStatus: investorDetailsNetworkStatus } = useQuery(GET_INVESTOR_DETAILS_DATA, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !ids.entityId,
    variables: {
      id: ids?.entityId
    },
    onCompleted: (data) => {
      const { entityType, category, eligibility, taxStatus, taxExempt, otherType } = data.legalEntity;
      const taxStatusValue = taxStatus
        ? EditInvestorEntityDetailsTaxStatusOptionEnum[taxStatus]
        : taxExempt
          ? EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAX_EXEMPT
          : EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAXABLE;

      setInvestorDetailsStepData({
        category: category || null,
        taxStatus: taxStatusValue,
        entityType: entityType || null,
        otherType: otherType || '',
        eligibility: eligibility?.length ? eligibility : []
      });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: renderingTemplatesLoading } = useQuery<{ renderingTemplates: IRenderingTemplate[] }>(RENDERING_TEMPLATES_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        defaults: true
      }
    },
    onCompleted: (data) => {
      setRenderingTemplates(data?.renderingTemplates ?? []);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { networkStatus: bankAccountsAndContactsNetworkStatus, refetch: bankAccountsAndContactsRefetch } = useQuery(
    GET_BANK_ACCOUNTS_AND_CONTACTS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      skip: !ids.entityId,
      variables: {
        id: ids.entityId
      },
      onCompleted: (data) => {
        const { contacts, bankAccounts } = data.legalEntity;
        setBankAccounts(bankAccounts);
        setContacts(contacts);
      }
    }
  );

  const { loading: getTenantLoading } = useQuery<{ getInvestor: { tenant: ITenant } }>(GET_TENANT_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !ids.familyId,
    variables: {
      data: {
        id: ids.familyId
      }
    },
    onCompleted: (data) => {
      setIds({
        ...ids,
        tenantId: data.getInvestor.tenant.id
      });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    if (allocationLoading || !allocationData) return;

    const { availableActions } = allocationData?.Allocation?.operationsWorkflowTransitionTask;
    if (availableActions?.length && !availableActions?.includes('generateSignaturePacket')) {
      navigate('/transactions');
    }
  }, [allocationLoading, allocationData]);

  useEffect(() => {
    if (renderingTemplates.length > 0) {
      setTemplate(renderingTemplates[0].template);
      setCurrentStepPage(renderingTemplates[0].template.pages[0]);
      setSignaturePacketWizardSteps(getInitialWizardSteps(renderingTemplates[0].template));
    }
  }, [renderingTemplates]);

  useEffect(() => {
    let isContactAndBankAccountComplete = true;
    const formData = methods.getValues();
    const currentStepFormData = formData?.[currentStepPage?.name] ?? {};
    const contactRows = currentStepPage?.rows.filter((row) => row.component === 'contacts');
    const bankAccountRows = currentStepPage?.rows.filter((row) => row.component === 'bankAccounts');

    if (contactRows?.length) {
      const selectedContacts =
        contactRows?.flatMap((row) => {
          const contactData = currentStepFormData?.[row.name];
          const contactsIDs = Array.isArray(contactData) ? contactData : [contactData];

          return contacts?.filter((contact) => contactsIDs?.includes(contact.id)).map((contact) => ({ contact, row }));
        }) ?? [];

      if (selectedContacts?.length) {
        isContactAndBankAccountComplete = !selectedContacts.some(({ row, contact }) => !isContactDataComplete(row, contact));
      }
    }

    if (bankAccountRows?.length) {
      const selectedBankAccounts = bankAccounts?.filter((bankAccount) => {
        return bankAccountRows?.some((row) => {
          const bankAccountData = currentStepFormData?.[row.name];
          const bankAccountIDs = Array.isArray(bankAccountData) ? bankAccountData : [bankAccountData];

          return bankAccountIDs?.includes(bankAccount.id);
        });
      });

      if (selectedBankAccounts?.length) {
        isContactAndBankAccountComplete = !selectedBankAccounts.some((bankAccount) => !isBankAccountDataComplete(bankAccount));
      }
    }

    setIsContactAndBankAccountComplete(isContactAndBankAccountComplete);
  }, [currentStepPage, currentRow, bankAccounts, contacts]);

  const fetchLegalEntityDocumentation = async (entityId: string) => {
    if (!entityId) return;

    try {
      setIsLoadingDocumentation(true);
      setErrorMessage(null);
      const { data } = await getLegalEntityDocumentation({
        variables: {
          id: entityId
        }
      });

      if (!data?.getLegalEntityDocumentation) return;

      const { templateId, ...rest } = data.getLegalEntityDocumentation;

      setIds((prevIds) => ({
        ...prevIds,
        templateId
      }));

      setKyc(rest);
    } catch (error) {
      console.error('Error fetching legal entity documentation:', error);
      setErrorMessage('Failed to load existing documentation data. Please try again.');
    } finally {
      setIsLoadingDocumentation(false);
    }
  };

  useEffect(() => {
    if (ids.entityId) {
      fetchLegalEntityDocumentation(ids.entityId);
    }
  }, [ids.entityId]);

  useQuery<{ investmentVehicles: InvestmentVehicleDto[] }>(INVESTMENT_VEHICLES_QUERY, {
    fetchPolicy: 'network-only',
    skip: !ids.investmentId,
    variables: {
      data: {
        investmentId: ids.investmentId,
        seriesCodes: []
      }
    },
    onCompleted: (data) => {
      setInvestmentVehicles(data?.investmentVehicles ?? []);
    }
  });

  const handleGenerateSubscriptionDocument = async (signatureType: SubscriptionDocumentSignatureType) => {
    try {
      setErrorMessage(null);
      setIsSubmitting(true);

      const dataForGenerateSubscriptionDocument = methods.getValues();
      await executeAction({
        variables: {
          data: {
            allocationId: params?.allocationId,
            action: 'generateSignaturePacket',
            actionDataPayload: {
              templateId: ids.templateId,
              allocationId: params?.allocationId,
              signatureType,
              data: dataForGenerateSubscriptionDocument
            }
          }
        }
      });

      goBack({ fallBack: '/transactions' });
    } catch (err) {
      const error = err as ApolloError;

      const forbiddenError = error?.graphQLErrors?.find((graphQLError) => graphQLError?.extensions?.code === 'FORBIDDEN');
      const fileTooLargeError = error?.graphQLErrors?.find((graphQLError) => graphQLError?.extensions?.code === 'FILE_TOO_LARGE');

      if (forbiddenError) {
        const { currentWorkflowTask, requestedAction } = forbiddenError?.extensions || {};

        if (currentWorkflowTask && requestedAction) {
          const formattedAction = _.startCase(requestedAction as string);
          const errorMessage = `${formattedAction} cannot be executed because the current task is ${currentWorkflowTask}.`;

          setErrorMessage(errorMessage);
        } else {
          setErrorMessage(defaultErrorMessage);
        }

        return;
      } else if (fileTooLargeError) {
        setErrorMessage(FILE_TOO_LARGE_ERROR_MESSAGE);
        return;
      }

      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignaturePacketWizardSteps = (steps: { name: string; status: string }[]) => {
    setSignaturePacketWizardSteps(steps);
  };

  const prevStep = () => {
    methods.clearErrors();
    updateWizardSteps('prev', signaturePacketWizardSteps, handleSignaturePacketWizardSteps);

    const currentStepPageIndex = template?.pages?.findIndex((page) => page.name === currentStepPage.name);
    if (currentStepPageIndex === 0) return;
    setCurrentStepPage(template.pages[currentStepPageIndex - 1]);
  };

  const nextStep = async (data?: any) => {
    try {
      setErrorMessage(null);
      setIsSubmitting(true);

      const currentStepIndex = signaturePacketWizardSteps.findIndex((step) => step.status === 'IN_PROGRESS');

      if (currentStepIndex + 1 === signaturePacketWizardSteps.length - 1) {
        if (data) {
          const dataToUpdate = {
            [currentStepPage.name]: {
              ...(data?.[currentStepPage.name] ?? {})
            }
          };

          await updateLegalEntityDocumentation({
            variables: {
              input: {
                id: ids?.entityId,
                templateId: ids?.templateId,
                kyc: dataToUpdate
              }
            }
          });

          setKyc(dataToUpdate);
        }
        updateWizardSteps('next', signaturePacketWizardSteps, handleSignaturePacketWizardSteps);
        return;
      }

      if (
        signaturePacketWizardSteps[currentStepIndex].name !== 'Investor Details' &&
        signaturePacketWizardSteps[currentStepIndex].name !== 'Transaction Details'
      ) {
        // get current page template
        const currentStepPageIndex = template?.pages?.findIndex((page) => page.name === currentStepPage.name);

        const page = template.pages[currentStepPageIndex];

        // Look for contact fields in the page
        const contactRows = page.rows.filter((row) => row.component === 'contacts');

        if (contactRows?.length) {
          const contacts = new Set<{ contactId: string; isInvestor: boolean }>();

          contactRows.forEach((row) => {
            if (data[page.name][row.name]) {
              const isInvestor = row.config.assignAsInvestorForFamily;
              // Handle both single contact and array of contacts
              const contactsData: { contactId: string; isInvestor: boolean }[] = Array.isArray(data[page.name][row.name])
                ? data[page.name][row.name].map((contact: string) => ({
                    contactId: contact,
                    isInvestor
                  }))
                : [{ contactId: data[page.name][row.name], isInvestor }];

              contactsData.forEach((contact) => {
                contacts.add(contact);
              });
            }
          });

          if (contacts.size > 0) {
            await upsertContactLegalEntity({
              variables: {
                input: {
                  contactIdInvestorStatus: Array.from(contacts),
                  legalEntityId: ids.entityId
                }
              }
            });
          }
        }

        if (data) {
          const dataToUpdate = {
            [currentStepPage.name]: {
              ...(data?.[currentStepPage.name] ?? {})
            }
          };

          await updateLegalEntityDocumentation({
            variables: {
              input: {
                id: ids?.entityId,
                templateId: ids?.templateId,
                kyc: dataToUpdate
              }
            }
          });

          const updatedKyc = {
            ...kyc,
            [currentStepPage.name]: {
              ...(data?.[currentStepPage.name] ?? {})
            }
          };

          setKyc(updatedKyc);
        }

        setCurrentStepPage(template.pages[currentStepPageIndex + 1]);
      }

      updateWizardSteps('next', signaturePacketWizardSteps, handleSignaturePacketWizardSteps);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openModalPage = ({
    type,
    row,
    id = null,
    currentAttachment = null,
    sectionName,
    expectedAttachmentName,
    contactId
  }: OpenWizardModalPageArgsType) => {
    setModalPage({ type, isOpen: true });
    setCurrentRow(row);
    setCurrentItemId(id);
    setCurrentAttachment(currentAttachment);
    setCurrentAttachmentSectionInfo({ sectionName, expectedAttachmentName, contactId });
    setScrollTo(window.scrollY);
  };

  const closeModalPage = async () => {
    await methods.trigger(`${currentStepPage?.name}.${currentRow?.name}`);
    setModalPage({ type: '', isOpen: false });
    setCurrentRow(null);
    setCurrentItemId(null);
    setCurrentAttachment(null);
    if (scrollTo) {
      setTimeout(() => window.scrollTo({ top: scrollTo, behavior: 'smooth' }), 0);
      setScrollTo(0);
    }
  };

  const fetchRenderingTemplates = async (investmentVehicleId: string) => {
    try {
      const { taxStatus, entityType, category } = investorDetailsStepData;
      const { data: renderingTemplatesByInvestmentVehicleId } = await getRenderingTemplates({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            investmentVehicleId,
            taxExempt: taxStatus === EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAX_EXEMPT,
            entityType: entityType as gqlEntityType,
            category: category as gqlCategory,
            ...(isPlatformManagerUser && { tenantId: ids.tenantId })
          }
        }
      });

      if (renderingTemplatesByInvestmentVehicleId?.renderingTemplates?.length) {
        const { template, id } = renderingTemplatesByInvestmentVehicleId.renderingTemplates[0];

        const formData = cleanFormDataByConfig(template?.pages ?? [], kyc || {});
        methods.reset((defaultValues) => ({ ...defaultValues, ...formData }));

        setIds({
          ...ids,
          templateId: id
        });
        setTemplate(template);
        setCurrentStepPage(template.pages[0]);
        setSignaturePacketWizardSteps(getWizardSteps(template));
        return;
      }

      const { data: renderingTemplatesByInvestmentId } = await getRenderingTemplates({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            investmentId: ids.investmentId,
            taxExempt: taxStatus === EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAX_EXEMPT,
            entityType: entityType as gqlEntityType,
            category: category as gqlCategory,
            ...(isPlatformManagerUser && { tenantId: ids.tenantId })
          }
        }
      });

      if (renderingTemplatesByInvestmentId?.renderingTemplates?.length) {
        const { template, id } = renderingTemplatesByInvestmentId.renderingTemplates[0];

        const formData = cleanFormDataByConfig(template?.pages ?? [], kyc || {});
        methods.reset((defaultValues) => ({ ...defaultValues, ...formData }));

        setIds({
          ...ids,
          templateId: id
        });
        setTemplate(template);
        setCurrentStepPage(template.pages[0]);
        setSignaturePacketWizardSteps(getWizardSteps(template));
        return;
      }

      const { data: renderingTemplatesDefaults } = await getRenderingTemplates({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            defaults: true,
            taxExempt: taxStatus === EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAX_EXEMPT,
            entityType: entityType as gqlEntityType,
            category: category as gqlCategory,
            ...(isPlatformManagerUser && { tenantId: ids.tenantId })
          }
        }
      });

      if (renderingTemplatesDefaults?.renderingTemplates?.length) {
        const { template, id } = renderingTemplatesDefaults.renderingTemplates[0];

        const formData = cleanFormDataByConfig(template?.pages ?? [], kyc || {});
        methods.reset((defaultValues) => ({ ...defaultValues, ...formData }));

        setIds({
          ...ids,
          templateId: id
        });
        setTemplate(template);
        setCurrentStepPage(template.pages[0]);
        setSignaturePacketWizardSteps(getWizardSteps(template));
        return;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getFieldValue = (name: string) => {
    return methods.getValues(name);
  };

  const saveFieldValue = (name: string, value: any) => {
    methods.setValue(name, value);
  };

  const unregisterField = (name: string) => {
    methods.unregister(name);
  };
  const saveInvestorDetailsData = async (data: IInvestorDetailsStepForm) => {
    try {
      setErrorMessage(null);
      setIsSubmitting(true);

      const { entityType, eligibility, otherType, taxStatus } = data;
      const taxStatusEnumKey: TaxStatus = getEnumKeyByValue(EditInvestorEntityDetailsTaxStatusOptionEnum, taxStatus) as TaxStatus;

      await updateEntity({
        variables: {
          data: {
            id: ids.entityId,
            ...data,
            taxStatus: taxStatusEnumKey,
            category: data.category as gqlCategory,
            eligibility: data.eligibility as gqlEligibility[],
            entityType: data.entityType as gqlEntityType,
            otherType: entityType === getEnumKeyByValue(EntityType, EntityType.Other) ? otherType : null
          }
        }
      });

      allocationRefetch();

      const updatedKyc = template?.pages?.reduce(
        (acc, page) => {
          if ('expectedData' in page) {
            const pageData = page.expectedData?.reduce(
              (pageAcc, expectedDataItem: any) => {
                return {
                  ...pageAcc,
                  [expectedDataItem.name]: eligibility
                };
              },
              acc?.[page.name] || {}
            );

            return {
              ...acc,
              [page.name]: pageData
            };
          }
          return acc;
        },
        { ...(kyc || {}) }
      );

      setKyc(updatedKyc);
      setInvestorDetailsStepData({ ...data, otherType: entityType === getEnumKeyByValue(EntityType, EntityType.Other) ? otherType : '' });
      nextStep();
    } catch (error) {
      console.error(error);
      window.scrollTo(0, 0);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveTransactionDetailsData = async (data: ITransactionDetailsStepForm) => {
    const { amount, vehicle } = data;
    try {
      setErrorMessage(null);
      setIsSubmitting(true);
      await updateAllocationAmount({
        variables: {
          data: {
            id: params?.allocationId,
            amount: parseFloat(amount)
          }
        }
      });

      await updateAllocationInvestmentVehicle({
        variables: {
          data: {
            id: params?.allocationId,
            investmentVehicleId: vehicle
          }
        }
      });

      await fetchRenderingTemplates(vehicle);
      allocationRefetch();
    } catch (error) {
      console.error(error);
      window.scrollTo(0, 0);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateAttachments = (): boolean => {
    if (!currentStepPage) return true;

    const formData = methods.getValues();
    const attachmentsRow = currentStepPage.rows.find((row) => row.component === 'attachments');
    if (!attachmentsRow) return true;

    const [schema] = processExpectedAttachments(formData, contacts, attachmentsRow.config);

    const attachmentsData = formData?.[currentStepPage.name]?.[attachmentsRow.name] ?? {};

    const flattenedAttachmentsData = Object.entries(attachmentsData).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item, idx) => {
            acc[`${key}[${idx}]`] = item;
          });
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const dataToValidate = { attachments: flattenedAttachmentsData };

    const result = schema.safeParse(dataToValidate);

    if (!result.success) {
      setModalWindow({ isOpen: true, type: 'missing-attachments' });
      return false;
    }
    return true;
  };

  const saveAndContinue = ({ withAttachmentsValidation }: { withAttachmentsValidation?: boolean } = {}) => {
    if (withAttachmentsValidation && currentStepPage?.name === 'addAttachments' && !validateAttachments()) {
      return;
    }

    methods.handleSubmit(nextStep, (error) => {
      console.log(error);
    })();
  };

  const handleGoBack = () => {
    if (signaturePacketWizardSteps?.[0]?.status === 'IN_PROGRESS') {
      goBack({ fallBack: '/transactions' });
      return;
    }
    prevStep();
  };

  if (
    renderingTemplatesLoading ||
    allocationNetworkStatus === NetworkStatus.loading ||
    allocationNetworkStatus === NetworkStatus.setVariables ||
    investorDetailsNetworkStatus === NetworkStatus.loading ||
    investorDetailsNetworkStatus === NetworkStatus.setVariables ||
    getTenantLoading ||
    isLoadingDocumentation ||
    bankAccountsAndContactsNetworkStatus === NetworkStatus.loading ||
    bankAccountsAndContactsNetworkStatus === NetworkStatus.setVariables
  ) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {modalPage.isOpen && modalPage.type === 'enter-bank-account-info' && currentRow && (
        <EnterBankAccountInfoModalPage
          row={currentRow}
          pageName={currentStepPage?.name ?? ''}
          currentItemId={currentItemId}
          tenantId={ids.tenantId}
          entityId={ids.entityId}
          bankAccounts={bankAccounts}
          mode="wizard"
          getBankAccountIds={getFieldValue}
          saveBankAccountIds={saveFieldValue}
          bankAccountsAndContactsRefetch={bankAccountsAndContactsRefetch}
          closeModalPage={closeModalPage}
        />
      )}
      {modalPage.isOpen && modalPage.type === 'enter-contact-info' && currentRow && (
        <EnterContactInfoModalPage
          row={currentRow}
          pageName={currentStepPage?.name ?? ''}
          currentItemId={currentItemId}
          tenantId={ids.tenantId}
          entityId={ids.entityId}
          contacts={contacts}
          currentEditPage={currentStepPage}
          getContactIds={getFieldValue}
          saveContactIds={saveFieldValue}
          bankAccountsAndContactsRefetch={bankAccountsAndContactsRefetch}
          closeModalPage={closeModalPage}
        />
      )}
      {modalPage.isOpen && modalPage.type === 'add-an-attachment-file' && (
        <AddAttachmentFileModalPage
          row={currentRow}
          pageName={currentStepPage?.name ?? ''}
          entityId={ids.entityId}
          contacts={contacts}
          currentAttachment={currentAttachment}
          formData={methods.getValues()}
          currentAttachmentSectionInfo={currentAttachmentSectionInfo}
          getFieldValue={getFieldValue}
          saveFieldValue={saveFieldValue}
          unregisterField={unregisterField}
          closeModalPage={closeModalPage}
        />
      )}
      {!modalPage.isOpen && (
        <>
          {isSubmitting && (
            <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
              <ModalLoader />
            </ModalWrappedContent>
          )}
          {modalWindow.isOpen && modalWindow.type === 'missing-attachments' && (
            <MissingAttachmentsWarning
              isOpen={modalWindow.isOpen}
              saveAndContinue={saveAndContinue}
              onClose={() => setModalWindow({ isOpen: false, type: '' })}
            />
          )}
          <MainWrap>
            <Header
              modalControl={
                <GoBackButton
                  handleClose={handleGoBack}
                  backToTitle={signaturePacketWizardSteps?.[0]?.status === 'IN_PROGRESS' ? 'Transactions' : ''}
                />
              }
            />
            <PageTitle title={allocationData?.Allocation?.investment?.name ?? ''} />
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            <Details detailsData={allocationData?.Allocation ?? null} withoutTabs />
            <PaddingWrap>
              {signaturePacketWizardSteps[0]?.status === 'IN_PROGRESS' && (
                <InvestorDetailsStep
                  mode="wizard"
                  signaturePacketWizardSteps={signaturePacketWizardSteps}
                  investorDetailsStepData={investorDetailsStepData}
                  saveInvestorDetailsData={saveInvestorDetailsData}
                  onCancel={() => goBack({ fallBack: '/transactions' })}
                />
              )}
              {signaturePacketWizardSteps[1]?.status === 'IN_PROGRESS' && (
                <TransactionDetailsStep
                  signaturePacketWizardSteps={signaturePacketWizardSteps}
                  investmentVehicles={investmentVehicles}
                  transactionDetailsStepData={{
                    committedCapital: allocationData?.Allocation?.committedCapital ?? 0,
                    proposalType: allocationData?.Allocation?.proposalType as PROPOSAL_TYPE,
                    minimum: allocationData?.Allocation?.investment?.minimum ?? 0,
                    investmentVehicleId: allocationData?.Allocation?.investmentVehicle?.id ?? ''
                  }}
                  saveTransactionDetailsData={saveTransactionDetailsData}
                  handleSignaturePacketWizardSteps={handleSignaturePacketWizardSteps}
                />
              )}
              {signaturePacketWizardSteps[0]?.status === 'COMPLETED' &&
                signaturePacketWizardSteps[1]?.status === 'COMPLETED' &&
                signaturePacketWizardSteps[signaturePacketWizardSteps.length - 1]?.status === 'IN_PROGRESS' && (
                  <SignatureMethodStep
                    signaturePacketWizardSteps={signaturePacketWizardSteps}
                    handleGenerateSubscriptionDocument={handleGenerateSubscriptionDocument}
                    handleSignaturePacketWizardSteps={handleSignaturePacketWizardSteps}
                  />
                )}
              {signaturePacketWizardSteps[0]?.status === 'COMPLETED' &&
                signaturePacketWizardSteps[1]?.status === 'COMPLETED' &&
                signaturePacketWizardSteps[signaturePacketWizardSteps.length - 1]?.status !== 'IN_PROGRESS' && (
                  <FormProvider {...methods}>
                    <StepWrapper isMobile={isMobile}>
                      <WizardProgressBar steps={signaturePacketWizardSteps} />
                      {currentStepPage?.rows?.map((row, index) => (
                        <RowComponent
                          key={`${row.name}-${index}`}
                          row={row}
                          mode="wizard"
                          pageName={currentStepPage?.name ?? ''}
                          pageData={currentStepPage}
                          entityId={ids.entityId}
                          contacts={contacts}
                          bankAccounts={bankAccounts}
                          openModalPage={openModalPage}
                        />
                      ))}
                    </StepWrapper>
                    <FooterModal
                      startOver={signaturePacketWizardSteps[0]?.status === 'COMPLETED' ? prevStep : null}
                      startOverButtonName={t('common:back')}
                      confirmButton={{
                        name: t('common:saveAndContinue'),
                        onClick: () => saveAndContinue({ withAttachmentsValidation: true }),
                        disabled: (methods.formState.isSubmitted && !methods.formState.isValid) || !isContactAndBankAccountComplete || isSubmitting
                      }}
                    />
                  </FormProvider>
                )}
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const StepWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 30px 0 150px 0;
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px 60px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;
